import React from 'react';

/**
 * The dark palette used across everything before the app itself: the landing
 * page, /pricing, and the sign-in screens.
 *
 * Rather than restyle each component, this remaps the APP's tokens to the
 * public palette for its whole subtree. Anything built against --content-*,
 * --surface-* or --accent-* therefore renders correctly here without knowing it
 * is on a dark page — which is what lets AuthForm come along unchanged.
 *
 * Why it matters: the app's theme defaults to `system`, so a visitor on a light
 * OS went from a near-black landing page to a white sign-in form mid-funnel.
 * The theme change now happens when the app appears, which reads as arriving
 * somewhere rather than as a flicker.
 */
export function PublicSurface({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`public-surface ${className}`}>
      <style>{`
        .public-surface {
          --ps-ground:   #0a0b0d;
          --ps-raise:    #101216;
          --ps-raise-2:  #16181e;
          --ps-grout:    rgba(255,255,255,.075);
          --ps-ink:      #f3f5f8;
          --ps-accent:   #E0A22B;

          /* App tokens, remapped for this subtree */
          --surface-primary:   var(--ps-ground);
          --surface-secondary: var(--ps-ground);
          --surface-tertiary:  var(--ps-raise-2);
          --surface-elevated:  var(--ps-raise);
          --content-primary:   var(--ps-ink);
          --content-secondary: rgba(243,245,248,.64);
          --content-tertiary:  rgba(243,245,248,.38);
          --content-inverted:  #0a0b0d;
          --border-primary:    var(--ps-grout);
          --border-secondary:  rgba(255,255,255,.05);
          --accent-primary:    var(--ps-accent);
          --accent-hover:      #EFB44A;
          --accent-subtle:     rgba(224,162,43,.14);
          --sidebar-hover:     rgba(255,255,255,.06);
          --input-bg:          var(--ps-raise);
          --input-border:      rgba(255,255,255,.12);
          --mosaic-dark:       var(--ps-ink);
          --status-error-bg:   rgba(228,102,79,.12);
          --error:             #E4664F;
          --shadow-lg:         0 24px 60px -28px rgba(0,0,0,.9);

          background: var(--ps-ground);
          color: var(--ps-ink);
          color-scheme: dark;
          min-height: 100vh;
        }

        /* The accent is gold, and white text on gold is unreadable */
        .public-surface button.bg-\\[var\\(--accent-primary\\)\\] { color: #0a0b0d; }

        /* One brand cue carried through from the landing page */
        .public-surface .ps-display {
          font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
          font-weight: 400;
          letter-spacing: -.012em;
        }
      `}</style>
      {children}
    </div>
  );
}
